import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useMRData } from "./MRDataProvider";
import GlobalButton from "../../common/GlobalButton";
import Swal from "sweetalert2";

export default function MRUpdateView({ mr, onClose, onSave }) {
  const { updateMR } = useMRData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [date, setDate] = useState(null);
  const [previousDate, setPreviousDate] = useState(null);
  const [noteTaker, setNoteTaker] = useState("");
  const [status, setStatus] = useState("Planned");

  // Section 1
  const [agendaItemA, setAgendaItemA] = useState("");
  const [agendaItemB, setAgendaItemB] = useState("");
  const [agendaItemC, setAgendaItemC] = useState("");

  // Section 2
  const [businessPerformance, setBusinessPerformance] = useState("");
  const [internalAuditResults, setInternalAuditResults] = useState("");
  const [supplierPerformance, setSupplierPerformance] = useState("");
  const [resourceAdequacy, setResourceAdequacy] = useState("");
  const [maintenanceIssues, setMaintenanceIssues] = useState("");
  const [customerFeedback, setCustomerFeedback] = useState("");

  // Sections 3-5
  const [risks, setRisks] = useState("");
  const [opportunities, setOpportunities] = useState("");
  const [businessUpdates, setBusinessUpdates] = useState("");

  // Section 6
  const [recapItemA, setRecapItemA] = useState("");
  const [recapItemB, setRecapItemB] = useState("");
  const [recapItemC, setRecapItemC] = useState("");

  // Parse date string to dayjs object
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;

    // Handle API date format (DD-MMM-YYYY)
    if (typeof dateStr === "string" && dateStr.includes("-")) {
      // Try different formats
      const formats = ["DD-MMM-YYYY", "YYYY-MM-DD", "MM/DD/YYYY"];

      for (const format of formats) {
        const parsed = dayjs(dateStr, format);
        if (parsed.isValid()) return parsed;
      }
    }

    // Default parsing
    const parsed = dayjs(dateStr);
    return parsed.isValid() ? parsed : null;
  };

  // Load the MR data
  useEffect(() => {
    if (mr) {
      // Dates
      setDate(parseDateString(mr.Date_of_this_Management_Review || mr.date));
      setPreviousDate(
        parseDateString(
          mr.Date_of_previous_Management_Review || mr.previousDate
        )
      );

      // Basic fields
      setNoteTaker(
        mr.Note_taker_for_this_Management_Review || mr.noteTaker || ""
      );
      setStatus(mr.Status || mr.status || "Planned");

      // Section 1
      setAgendaItemA(mr.a1 || mr.agendaItems?.a || "");
      setAgendaItemB(mr.b1 || mr.agendaItems?.b || "");
      setAgendaItemC(mr.c1 || mr.agendaItems?.c || "");

      // Section 2
      setBusinessPerformance(
        mr.a2 || mr.agendaItems?.businessPerformance || ""
      );
      setInternalAuditResults(
        mr.b2 || mr.agendaItems?.internalAuditResults || ""
      );
      setSupplierPerformance(
        mr.c2 || mr.agendaItems?.supplierPerformance || ""
      );
      setResourceAdequacy(mr.d2 || mr.agendaItems?.resourceAdequacy || "");
      setMaintenanceIssues(mr.e2 || mr.agendaItems?.maintenanceIssues || "");
      setCustomerFeedback(mr.f2 || mr.agendaItems?.customerFeedback || "");

      // Sections 3-5
      setRisks(mr.a3 || mr.agendaItems?.risks || "");
      setOpportunities(mr.a4 || mr.agendaItems?.opportunities || "");
      setBusinessUpdates(mr.a5 || mr.agendaItems?.businessUpdates || "");

      // Section 6
      setRecapItemA(mr.a6 || mr.agendaItems?.recapItemA || "");
      setRecapItemB(mr.b6 || mr.agendaItems?.recapItemB || "");
      setRecapItemC(mr.c6 || mr.agendaItems?.recapItemC || "");
    }
  }, [mr]);

  // Handle form submission
  const handleUpdate = async () => {
    if (!date) {
      setError("Date is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const mrData = {
        // Basic fields
        date: date.format("YYYY-MM-DD"),
        previousDate: previousDate ? previousDate.format("YYYY-MM-DD") : null,
        noteTaker,
        status,

        // Structured as needed by the API adapter
        agendaItems: {
          a: agendaItemA,
          b: agendaItemB,
          c: agendaItemC,
          businessPerformance,
          internalAuditResults,
          supplierPerformance,
          resourceAdequacy,
          maintenanceIssues,
          customerFeedback,
          risks,
          opportunities,
          businessUpdates,
          recapItemA,
          recapItemB,
          recapItemC,
        },
      };

      const success = await updateMR(mr.ID || mr.id, mrData);

      if (success) {
        Swal.fire({
          title: "Success",
          text: "Management Review updated successfully!",
          icon: "success",
          confirmButtonText: "OK",
        });

        if (onSave) {
          onSave();
        }
      } else {
        setError("Failed to update management review");
      }
    } catch (err) {
      setError("Failed to update management review: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form fields to their initial values from the loaded MR
  const handleReset = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!mr) return null;

  return (
    <Box sx={{ maxWidth: "1100px", mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Update Management Review
        </Typography>
        <IconButton onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of this Management Review"
              value={date}
              onChange={(newDate) => setDate(newDate)}
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "medium",
                  sx: { fontSize: "1rem" },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of previous Management Review"
              value={previousDate}
              onChange={(newDate) => setPreviousDate(newDate)}
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "medium",
                  sx: { fontSize: "1rem" },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Note taker for this Management Review"
            fullWidth
            value={noteTaker}
            onChange={(e) => setNoteTaker(e.target.value)}
            disabled={loading}
            size="medium"
            sx={{ fontSize: "1rem" }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: "1rem" }}>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label="Status"
              disabled={loading}
              sx={{ fontSize: "1rem" }}
            >
              <MenuItem value="Planned" sx={{ fontSize: "1rem" }}>
                Planned
              </MenuItem>
              <MenuItem value="Completed" sx={{ fontSize: "1rem" }}>
                Completed
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Paper
        sx={{
          p: 3,
          mb: 2,
          //bgcolor: "#f5f5f5",
          //borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="medium" sx={{ mb: 2 }}>
          Agenda
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            mb: 2,
            fontWeight: 500,
            borderTop: "1px solid #e0e0e0",
            pt: 1,
          }}
        />
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          1. Review actions from prior Management Review
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="a"
              fullWidth
              multiline
              rows={4}
              value={agendaItemA}
              onChange={(e) => setAgendaItemA(e.target.value)}
              disabled={loading}
              placeholder="Enter agenda item details"
              size="medium"
              sx={{ mb: 2, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "1rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="b"
              fullWidth
              multiline
              rows={4}
              value={agendaItemB}
              onChange={(e) => setAgendaItemB(e.target.value)}
              disabled={loading}
              placeholder="Enter agenda item details"
              size="medium"
              sx={{ mb: 2, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "1rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="c"
              fullWidth
              multiline
              rows={4}
              value={agendaItemC}
              onChange={(e) => setAgendaItemC(e.target.value)}
              disabled={loading}
              placeholder="Enter agenda item details"
              size="medium"
              sx={{ mb: 2, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "1rem" },
              }}
            />
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            mb: 2,
            fontWeight: 500,
            borderTop: "1px solid #e0e0e0",
            pt: 1,
          }}
        />
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          2. Review and discuss
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="a. Business performance and metrics"
              fullWidth
              multiline
              rows={4}
              value={businessPerformance}
              onChange={(e) => setBusinessPerformance(e.target.value)}
              disabled={loading}
              placeholder="Enter business performance details"
              size="medium"
              sx={{ mb: 1, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="b. Internal Audit results"
              fullWidth
              multiline
              rows={4}
              value={internalAuditResults}
              onChange={(e) => setInternalAuditResults(e.target.value)}
              disabled={loading}
              placeholder="Enter internal audit results"
              size="medium"
              sx={{ mb: 0, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="c. Supplier performance"
              fullWidth
              multiline
              rows={4}
              value={supplierPerformance}
              onChange={(e) => setSupplierPerformance(e.target.value)}
              disabled={loading}
              placeholder="Enter supplier performance details"
              size="medium"
              sx={{ mb: 0, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="d. Adequacy of resources"
              fullWidth
              multiline
              rows={4}
              value={resourceAdequacy}
              onChange={(e) => setResourceAdequacy(e.target.value)}
              disabled={loading}
              placeholder="People, material, equipment, space"
              size="medium"
              sx={{ mb: 0, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="e. Review maintenance issues"
              fullWidth
              multiline
              rows={4}
              value={maintenanceIssues}
              onChange={(e) => setMaintenanceIssues(e.target.value)}
              disabled={loading}
              placeholder="Enter maintenance issues"
              size="medium"
              sx={{ mb: 0, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="f. Customer feedback"
              fullWidth
              multiline
              rows={4}
              value={customerFeedback}
              onChange={(e) => setCustomerFeedback(e.target.value)}
              disabled={loading}
              placeholder="Once a quarter, when feedback is sent-out"
              size="medium"
              sx={{ mb: 2, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            mb: 1,
            fontWeight: 500,
            borderTop: "1px solid #e0e0e0",
            pt: 1,
          }}
        />
        <Grid container spacing={3} sx={{ mt: 2, mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              3. Risks
            </Typography>
            <TextField
              label="Customer loss, machine health, facilities health"
              fullWidth
              multiline
              rows={4}
              value={risks}
              onChange={(e) => setRisks(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ mb: 0, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              4. Opportunities for improvement
            </Typography>
            <TextField
              label="Opportunities for improvement"
              fullWidth
              multiline
              rows={4}
              value={opportunities}
              onChange={(e) => setOpportunities(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ mb: 0, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              5. Other business updates
            </Typography>
            <TextField
              label="Open forum for everyone for updates or questions"
              fullWidth
              multiline
              rows={4}
              value={businessUpdates}
              onChange={(e) => setBusinessUpdates(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ mb: 2, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "0.9rem" },
              }}
            />
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            mb: 1,
            fontWeight: 500,
            borderTop: "1px solid #e0e0e0",
            pt: 1,
          }}
        />
        <Typography variant="body1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
          6. Recap action items from this Management Review (with name and date)
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="a"
              fullWidth
              multiline
              rows={4}
              value={recapItemA}
              onChange={(e) => setRecapItemA(e.target.value)}
              disabled={loading}
              placeholder="Enter action item with name and date"
              size="medium"
              sx={{ mb: 3, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "1rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="b"
              fullWidth
              multiline
              rows={4}
              value={recapItemB}
              onChange={(e) => setRecapItemB(e.target.value)}
              disabled={loading}
              placeholder="Enter action item with name and date"
              size="medium"
              sx={{ mb: 3, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "1rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="c"
              fullWidth
              multiline
              rows={4}
              value={recapItemC}
              onChange={(e) => setRecapItemC(e.target.value)}
              disabled={loading}
              placeholder="Enter action item with name and date"
              size="medium"
              sx={{ mb: 3, fontSize: "1rem" }}
              InputProps={{
                sx: { fontSize: "1rem" },
              }}
              InputLabelProps={{
                sx: { fontSize: "1rem" },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <GlobalButton
          variant="outlined"
          onClick={handleReset}
          disabled={loading}
          icon={<RestartAltIcon />}
          sx={{ mr: 2 }}
        >
          RESET
        </GlobalButton>

        <GlobalButton
          variant="contained"
          onClick={handleUpdate}
          disabled={loading}
          icon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {loading ? "Saving..." : "Save"}
        </GlobalButton>
      </Box>
    </Box>
  );
}
